// React/Next imports
import { useCallback, useEffect, useRef, useState } from 'react';
import { usePopper } from 'react-popper';

// Third-party libraries
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import clsx from 'clsx';
import { useAtom } from 'jotai';
import { isEmpty } from 'lodash';

// Hooks
import useSubscription from '@/utils/hooks/swr/pricing/useSubscription';
import useUser from '@/utils/hooks/swr/useUser';
import useTranslation from '@/utils/hooks/translation/useTranslation';

import { injectCssForProductFruitChecklistDarkMode } from '@/components/layouts/LayoutPlatform/components/Header/components/OnboardingTasks/utils';
import useDarkMode from '@/utils/hooks/utils/useDarkMode';

// Utils
import { listIsClosedManuallyAtom } from '@/components/layouts/LayoutPlatform/components/Header/components/OnboardingTasks/atoms';

// Components
import Icon from '@/components/base/Icon';

const CHECKLIST_IDS = {
    en: process.env.NEXT_PUBLIC_PRODUCT_FRUITS_CHECKLIST_ID_EN,
    fr: process.env.NEXT_PUBLIC_PRODUCT_FRUITS_CHECKLIST_ID_FR,
    es: process.env.NEXT_PUBLIC_PRODUCT_FRUITS_CHECKLIST_ID_ES
} as const;

const PRODUCT_FRUITS_CHECK_INTERVAL = 100;

const OnboardingTasks = () => {
    const { isOnTrial } = useSubscription();
    const { user } = useUser();
    const { t, lang } = useTranslation('layout_platform');
    const { isDarkMode } = useDarkMode();

    const [checkListIsOpen, setCheckListIsOpen] = useState<boolean>(false);
    const [loaded, setLoaded] = useState<boolean>(false);
    const [listIsClosedManually, setListIsClosedManually] = useAtom(listIsClosedManuallyAtom);

    const [referenceElement, setReferenceElement] = useState<HTMLButtonElement | null>(null);
    const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);

    const hasInitializedChecklistState = useRef<boolean>(false);
    const previousUserIdRef = useRef<number | undefined>(undefined);
    const disposeEvents = useRef<Record<string, () => void>>({});

    const { styles, attributes } = usePopper(referenceElement, popperElement, {
        placement: 'bottom-end',
        modifiers: [{ name: 'offset', options: { offset: [4, 12] } }]
    });

    const getChecklistId = () => {
        return CHECKLIST_IDS[lang as keyof typeof CHECKLIST_IDS] || CHECKLIST_IDS.en;
    };

    const initProductFruitCheckList = useCallback(() => {
        const checklistId = getChecklistId();
        const checkListContainer = document.getElementById('product-fruit-checklist');

        if (!window?.productFruits?.api?.checklists || !checkListContainer || !checklistId) {
            return;
        }

        try {
            window?.productFruits.api.checklists.injectToElement(checklistId, checkListContainer);
            if (isDarkMode) {
                injectCssForProductFruitChecklistDarkMode();
            }
        } catch (error) {
            // Silently fail - Product Fruits checklist injection is not critical
            if (process.env.NODE_ENV !== 'production') {
                console.debug('Product Fruits checklist injection failed', error);
            }
        }
    }, [isDarkMode, lang]);

    const disposeProductFruitsEvents = useCallback(() => {
        Object.values(disposeEvents.current).forEach((dispose) => dispose?.());
        disposeEvents.current = {};
    }, []);

    const handleProductFruitsEvents = useCallback(() => {
        // Set up listeners only once
        if (!window?.productFruits?.api?.tours || !isEmpty(disposeEvents.current)) return;

        const eventHandlers = {
            'tour-advanced': () => setCheckListIsOpen(false),
            'tour-finished': () => setCheckListIsOpen(true),
            'tour-skipped': () => setCheckListIsOpen(true)
        };

        Object.entries(eventHandlers).forEach(([event, handler]) => {
            disposeEvents.current[event] = window?.productFruits.api.tours.listen(event, handler);
        });
    }, []);

    const toggleChecklist = () => {
        setCheckListIsOpen((prevState) => !prevState);
        setListIsClosedManually((prevState) => !prevState);
    };

    // Reset initialization state when user changes (logout/login)
    useEffect(() => {
        if (user?.id !== previousUserIdRef.current) {
            if (previousUserIdRef.current !== undefined) {
                hasInitializedChecklistState.current = false;

                setListIsClosedManually(false);
                disposeProductFruitsEvents();
            }

            previousUserIdRef.current = user?.id;
        }
    }, [user?.id, setListIsClosedManually, disposeProductFruitsEvents]);

    // Check if list is completed
    useEffect(() => {
        if (!loaded || !window?.productFruits?.api) return;

        const checklistId = getChecklistId();
        const checkLists = window?.productFruits.api.checklists?.getChecklists();
        const activeCheckList = checkLists?.find(
            (list) => list.id === Number.parseInt(checklistId, 10)
        );

        const isCompleted = activeCheckList?.state === 'finished';

        if (isCompleted || listIsClosedManually) {
            setCheckListIsOpen(false);
        } else if (!hasInitializedChecklistState.current && isOnTrial) {
            setCheckListIsOpen(true);
        }

        if (!hasInitializedChecklistState.current) {
            hasInitializedChecklistState.current = true;
        }

        handleProductFruitsEvents();
    }, [loaded, isOnTrial, handleProductFruitsEvents, lang, listIsClosedManually]);

    // Initialize ProductFruits
    useEffect(() => {
        if (window?.productFruits?.api) {
            setLoaded(true);
            return;
        }

        const checkInterval = setInterval(() => {
            if (window.productFruits?.api) {
                setLoaded(true);
                clearInterval(checkInterval);
            }
        }, PRODUCT_FRUITS_CHECK_INTERVAL);

        return () => clearInterval(checkInterval);
    }, []);

    // Initialize checklist when opened
    useEffect(() => {
        if (checkListIsOpen) {
            initProductFruitCheckList();
        }
    }, [checkListIsOpen, initProductFruitCheckList]);

    return (
        <Popover className="relative">
            <PopoverButton
                ref={setReferenceElement}
                as="button"
                onClick={toggleChecklist}
                data-testid="onboarding-tasks-button"
                title={t('onboarding.get_started')}
                className={clsx(
                    'relative flex h-auto items-center justify-center gap-2 rounded-full bg-icoGray-100 p-2 dark:bg-icoBlack sm:p-4',
                    {
                        'bg-icoBlue': checkListIsOpen,
                        'bg-icoGray-100': !checkListIsOpen
                    }
                )}>
                <Icon className="text-icoBlue dark:text-white" icon="checklist" />
                <div className="hidden items-center gap-2 font-GSemiBold text-xs sm:flex">
                    {t('onboarding.get_started')}
                </div>
                <Icon
                    className="hidden text-icoGray-400 sm:block"
                    icon={checkListIsOpen ? 'chevron-top' : 'chevron-bottom'}
                />
            </PopoverButton>

            {/* Trial user, uncompleted checklist */}
            {checkListIsOpen && (
                <PopoverPanel
                    static
                    ref={(element) => setPopperElement(element as HTMLDivElement)}
                    style={styles.popper}
                    {...attributes.popper}>
                    <div
                        data-testid="onboarding-tasks-popover"
                        id="product-fruit-checklist"
                        className="h-500 absolute bottom-0 right-0 w-87.5 rounded-2xl"></div>
                </PopoverPanel>
            )}
        </Popover>
    );
};

export default OnboardingTasks;
